import {ReactNode, useEffect, useState} from "react"
import {ChangelogContext} from "@/components/changelog/context/changelog-context"
import {useQuery} from "@tanstack/react-query"
import {pckChangelog, pckProductVersions} from "@/library/api/changelog-requests"
import {Product, ProductVersion} from "@/library/api/models/changelog"
import {useParams} from "@tanstack/react-router"
import useAuth from "@/library/auth/_hook.auth"

const ChangelogProvider = ({children, withAuth}: { children?: ReactNode, withAuth?: boolean }) => {
    const auth = useAuth()
    const {refKey} = useParams({strict: false})
    const [currentProduct, setCurrentProduct] = useState<Product | undefined>()
    const [products, setProducts] = useState<Product[]>([])
    const [versions, setVersions] = useState<ProductVersion[]>([])
    const [isLoading, setIsLoading] = useState(true)

    const productsQuery = useQuery<Product[]>({
        queryKey: ["products"],
        queryFn: pckChangelog
    })

    const versionsQuery = useQuery<ProductVersion[]>({
        queryKey: ["versions", currentProduct?.refKey],
        queryFn: () => pckProductVersions(currentProduct!.refKey, withAuth == false ? undefined : auth.token),
        enabled: currentProduct != undefined && !auth.isLoading
    })

    useEffect(() => {
        if (!productsQuery.data)
            return

        setProducts(() => productsQuery.data)
    }, [productsQuery.data])

    useEffect(() => {
        if (products.length <= 0)
            return

        setCurrentProduct(() =>
            products.find(p => p.refKey == refKey) ?? products.first()
        )
    }, [refKey, products])

    useEffect(() => {
        if (!versionsQuery.data)
            return

        setVersions(() => versionsQuery.data)
    }, [currentProduct, versionsQuery.data])

    useEffect(() => {
        setIsLoading(() => versionsQuery.isLoading || productsQuery.isLoading)
    }, [versionsQuery, productsQuery])

    useEffect(() => {
        if (isLoading)
            return

        selectProduct(refKey)
    }, [refKey, isLoading])

    const selectProduct = (refKey: string) => {
        const product = products.find(p => p.refKey == refKey)
        if (!product)
            return

        setCurrentProduct(() => product)
    }

    return <ChangelogContext.Provider
        value={{
            currentProduct: currentProduct,
            products: products,
            versions: versions,
            isLoading: isLoading,
            withAuth: withAuth,
            selectProduct: selectProduct
        }}
    >{children}</ChangelogContext.Provider>
}

export default ChangelogProvider
